import React, { useState, useEffect } from 'react';

const DEFAULTS = {
  product: '', slug: '', isActive: true,
  heroImage: '', galleryImages: [], description: '',
  backgroundType: 'color', backgroundColor: '#141414', backgroundImage: '', backgroundOverlay: 0.7,
  buttonText: 'Play Now', buttonEnabled: true, buttonDisabledText: 'Coming Soon',
  buttonColor: '#ffffff', buttonTextColor: '#111111',
  titleColor: '#ffffff', descriptionColor: 'rgba(255, 255, 255, 0.65)', showMetaTags: true,
  metaTitle: '', metaDescription: '',
};

const ManageLandingPages = ({ backendUrl, resetKey }) => {
  const [landingPages, setLandingPages] = useState([]);
  const [products, setProducts] = useState([]);
  const [newLP, setNewLP] = useState({ ...DEFAULTS });
  const [selectedLP, setSelectedLP] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });
  const [slugFilter, setSlugFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    fetchLandingPages();
    fetchProducts();
    setSelectedLP(null);
    setNewLP({ ...DEFAULTS });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  const fetchLandingPages = async () => {
    const token = localStorage.getItem('auth-token');
    try {
      const response = await fetch(`${backendUrl}/landing-page/get`, {
        headers: { 'Content-Type': 'application/json', 'auth-token': token },
      });
      const result = await response.json();
      if (result.success) setLandingPages(result.landingPages || []);
    } catch (error) {
      console.error('Error fetching landing pages:', error);
    }
  };

  const fetchProducts = async () => {
    const token = localStorage.getItem('auth-token');
    try {
      const response = await fetch(`${backendUrl}/product/get`, {
        headers: { 'Content-Type': 'application/json', 'auth-token': token },
      });
      const result = await response.json();
      if (result.success) setProducts(result.products || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const handleProductSelect = async (productId) => {
    setNewLP(prev => ({ ...prev, product: productId }));
    if (!productId) return;
    const product = products.find(p => p._id === productId);
    if (!product) return;
    const token = localStorage.getItem('auth-token');
    try {
      const response = await fetch(
        `${backendUrl}/landing-page/suggest-slug?title=${encodeURIComponent(product.title)}`,
        { headers: { 'Content-Type': 'application/json', 'auth-token': token } }
      );
      const result = await response.json();
      if (result.success) {
        setNewLP(prev => ({
          ...prev, product: productId, slug: result.slug,
          metaTitle: product.title, metaDescription: product.description || '',
        }));
      }
    } catch (error) {
      console.error('Error suggesting slug:', error);
    }
  };

  const buildPayload = (data) => ({
    product: data.product?._id || data.product,
    slug: data.slug,
    isActive: !!data.isActive,
    heroImage: data.heroImage || '',
    galleryImages: Array.isArray(data.galleryImages) ? data.galleryImages.filter(Boolean) : [],
    description: data.description || '',
    backgroundType: data.backgroundType || 'color',
    backgroundColor: data.backgroundColor || '#141414',
    backgroundImage: data.backgroundImage || '',
    backgroundOverlay: typeof data.backgroundOverlay === 'number' ? data.backgroundOverlay : 0.7,
    buttonText: data.buttonText || 'Play Now',
    buttonEnabled: typeof data.buttonEnabled === 'boolean' ? data.buttonEnabled : true,
    buttonDisabledText: data.buttonDisabledText || 'Coming Soon',
    buttonColor: data.buttonColor || '#ffffff',
    buttonTextColor: data.buttonTextColor || '#111111',
    titleColor: data.titleColor || '#ffffff',
    descriptionColor: data.descriptionColor || 'rgba(255, 255, 255, 0.65)',
    showMetaTags: typeof data.showMetaTags === 'boolean' ? data.showMetaTags : true,
    metaTitle: data.metaTitle,
    metaDescription: data.metaDescription,
  });

  const createLandingPage = async () => {
    if (!newLP.product) { alert('Please select a product.'); return; }
    if (!newLP.slug) { alert('Please enter a slug.'); return; }
    const token = localStorage.getItem('auth-token');
    try {
      const response = await fetch(`${backendUrl}/landing-page/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'auth-token': token },
        body: JSON.stringify(buildPayload(newLP)),
      });
      const result = await response.json();
      if (result.success) {
        alert('Landing page created successfully!');
        setNewLP({ ...DEFAULTS });
        fetchLandingPages();
      } else {
        alert(result.message || 'Error creating landing page.');
      }
    } catch (error) {
      console.error('Error creating landing page:', error);
    }
  };

  const updateLandingPage = async () => {
    const token = localStorage.getItem('auth-token');
    try {
      const response = await fetch(`${backendUrl}/landing-page/update/${selectedLP._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'auth-token': token },
        body: JSON.stringify(buildPayload(selectedLP)),
      });
      const result = await response.json();
      if (result.success) {
        alert('Landing page updated successfully!');
        setSelectedLP(null);
        fetchLandingPages();
      } else {
        alert(result.message || 'Error updating landing page.');
      }
    } catch (error) {
      console.error('Error updating landing page:', error);
    }
  };

  const deleteLandingPage = async (id) => {
    const token = localStorage.getItem('auth-token');
    try {
      const response = await fetch(`${backendUrl}/landing-page/delete/${id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', 'auth-token': token },
      });
      const result = await response.json();
      if (result.success) {
        alert('Landing page deleted successfully!');
        setSelectedLP(null);
        fetchLandingPages();
      } else {
        alert(result.message || 'Error deleting landing page.');
      }
    } catch (error) {
      console.error('Error deleting landing page:', error);
    }
  };

  const sortLandingPages = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') direction = 'descending';
    setSortConfig({ key, direction });
  };

  const filteredLandingPages = React.useMemo(() => {
    let filtered = [...landingPages];
    if (slugFilter) filtered = filtered.filter(lp => (lp.slug || '').toLowerCase().includes(slugFilter.toLowerCase()));
    if (statusFilter) { const b = statusFilter === 'Active'; filtered = filtered.filter(lp => lp.isActive === b); }
    return filtered;
  }, [landingPages, slugFilter, statusFilter]);

  const sortedLandingPages = React.useMemo(() => {
    let sortable = [...filteredLandingPages];
    if (sortConfig.key) {
      sortable.sort((a, b) => {
        let aV = a[sortConfig.key], bV = b[sortConfig.key];
        if (sortConfig.key === 'productTitle') { aV = a.product?.title || ''; bV = b.product?.title || ''; }
        if (sortConfig.key === 'isActive') { aV = aV ? 1 : 0; bV = bV ? 1 : 0; }
        if (aV < bV) return sortConfig.direction === 'ascending' ? -1 : 1;
        if (aV > bV) return sortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
      });
    }
    return sortable;
  }, [filteredLandingPages, sortConfig]);

  // Reusable form for create/edit
  const renderForm = (data, setData, isEdit) => {
    const handleChange = (e) => { const { name, value } = e.target; setData(prev => ({ ...prev, [name]: value })); };
    const selectedProduct = products.find(p => p._id === (data.product?._id || data.product));

    return (
      <>
        <label>Product</label>
        {isEdit ? (
          <select value={data.product?._id || data.product} onChange={handleChange} name="product">
            <option value="">Select Product</option>
            {products.map(p => <option key={p._id} value={p._id}>{p.title}</option>)}
          </select>
        ) : (
          <select value={data.product} onChange={(e) => handleProductSelect(e.target.value)} name="product">
            <option value="">Select Product</option>
            {products.map(p => <option key={p._id} value={p._id}>{p.title}</option>)}
          </select>
        )}

        <label>URL Slug</label>
        <input type="text" name="slug" value={data.slug} onChange={handleChange} placeholder="e.g. my-concert" />

        <label>Status</label>
        <div className="radio-group">
          <input type="radio" id={`${isEdit ? 'e' : 'n'}ActiveY`} checked={data.isActive === true}
            onChange={() => setData(prev => ({ ...prev, isActive: true }))} />
          <label htmlFor={`${isEdit ? 'e' : 'n'}ActiveY`} className={`radio-label ${data.isActive === true ? 'selected' : ''}`}>Active</label>
          <input type="radio" id={`${isEdit ? 'e' : 'n'}ActiveN`} checked={data.isActive === false}
            onChange={() => setData(prev => ({ ...prev, isActive: false }))} />
          <label htmlFor={`${isEdit ? 'e' : 'n'}ActiveN`} className={`radio-label ${data.isActive === false ? 'selected' : ''}`}>Inactive</label>
        </div>

        <h4 style={{ marginTop: '24px', marginBottom: '8px', color: '#9a51e2', borderBottom: '1px solid #333', paddingBottom: '8px' }}>
          Content
        </h4>

        <label>Gallery Images <span style={{ color: '#888', fontSize: '12px' }}>(if empty, product image is used)</span></label>
        {(data.galleryImages || []).map((url, idx) => (
          <div key={idx} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
            <input type="text" value={url}
              onChange={(e) => {
                const updated = [...(data.galleryImages || [])];
                updated[idx] = e.target.value;
                setData(prev => ({ ...prev, galleryImages: updated }));
              }}
              placeholder="Image URL" style={{ flex: 1 }} />
            <button type="button" onClick={() => {
              const updated = (data.galleryImages || []).filter((_, i) => i !== idx);
              setData(prev => ({ ...prev, galleryImages: updated }));
            }} style={{
              padding: '6px 12px', backgroundColor: '#f56565', color: 'white', border: 'none',
              borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap'
            }}>Remove</button>
          </div>
        ))}
        <button type="button" onClick={() => {
          setData(prev => ({ ...prev, galleryImages: [...(prev.galleryImages || []), ''] }));
        }} style={{
          padding: '8px 16px', backgroundColor: '#2d3748', color: '#e2e8f0', border: '1px solid #555',
          borderRadius: '6px', cursor: 'pointer', fontSize: '13px', marginBottom: '8px'
        }}>+ Add Image</button>

        {(data.galleryImages || []).filter(Boolean).length > 0 && (
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px', marginBottom: '12px' }}>
            {data.galleryImages.filter(Boolean).map((url, idx) => (
              <img key={idx} src={url} alt={`Gallery ${idx + 1}`}
                style={{ width: '100px', height: '70px', borderRadius: '6px', border: '1px solid #444', objectFit: 'cover' }} />
            ))}
          </div>
        )}

        {(data.galleryImages || []).filter(Boolean).length === 0 && selectedProduct?.featuredImage && (
          <div style={{ marginTop: '4px', marginBottom: '12px' }}>
            <img src={selectedProduct.featuredImage} alt="Product"
              style={{ maxWidth: '200px', maxHeight: '120px', borderRadius: '6px', border: '1px solid #444', objectFit: 'cover' }} />
            <span style={{ display: 'block', fontSize: '11px', color: '#888', marginTop: '4px' }}>From product (default)</span>
          </div>
        )}

        <label>Description <span style={{ color: '#888', fontSize: '12px' }}>(empty = use product description)</span></label>
        <textarea name="description" value={data.description} onChange={handleChange}
          placeholder={selectedProduct?.description || 'Custom description or leave empty for product description'} />

        <label>Show Meta Tags <span style={{ color: '#888', fontSize: '12px' }}>(projection, cameras, audio)</span></label>
        <div className="radio-group">
          <input type="radio" id={`${isEdit ? 'e' : 'n'}MetaY`} checked={data.showMetaTags !== false}
            onChange={() => setData(prev => ({ ...prev, showMetaTags: true }))} />
          <label htmlFor={`${isEdit ? 'e' : 'n'}MetaY`} className={`radio-label ${data.showMetaTags !== false ? 'selected' : ''}`}>Show</label>
          <input type="radio" id={`${isEdit ? 'e' : 'n'}MetaN`} checked={data.showMetaTags === false}
            onChange={() => setData(prev => ({ ...prev, showMetaTags: false }))} />
          <label htmlFor={`${isEdit ? 'e' : 'n'}MetaN`} className={`radio-label ${data.showMetaTags === false ? 'selected' : ''}`}>Hide</label>
        </div>

        <h4 style={{ marginTop: '24px', marginBottom: '8px', color: '#9a51e2', borderBottom: '1px solid #333', paddingBottom: '8px' }}>
          Background
        </h4>

        <label>Background Type</label>
        <div className="radio-group">
          <input type="radio" id={`${isEdit ? 'e' : 'n'}BgColor`} checked={data.backgroundType !== 'image'}
            onChange={() => setData(prev => ({ ...prev, backgroundType: 'color' }))} />
          <label htmlFor={`${isEdit ? 'e' : 'n'}BgColor`} className={`radio-label ${data.backgroundType !== 'image' ? 'selected' : ''}`}>Color</label>
          <input type="radio" id={`${isEdit ? 'e' : 'n'}BgImage`} checked={data.backgroundType === 'image'}
            onChange={() => setData(prev => ({ ...prev, backgroundType: 'image' }))} />
          <label htmlFor={`${isEdit ? 'e' : 'n'}BgImage`} className={`radio-label ${data.backgroundType === 'image' ? 'selected' : ''}`}>Image</label>
        </div>

        {data.backgroundType === 'image' ? (
          <>
            <label>Background Image URL</label>
            <input type="text" name="backgroundImage" value={data.backgroundImage || ''} onChange={handleChange} placeholder="https://example.com/background.jpg" />
            {data.backgroundImage && (
              <div style={{ marginTop: '8px', marginBottom: '12px' }}>
                <img src={data.backgroundImage} alt="Background preview"
                  style={{ maxWidth: '300px', maxHeight: '120px', borderRadius: '6px', border: '1px solid #444', objectFit: 'cover', width: '100%' }} />
              </div>
            )}
            <label>Dark Overlay Opacity <span style={{ color: '#888', fontSize: '12px' }}>({Math.round((data.backgroundOverlay ?? 0.7) * 100)}%)</span></label>
            <input type="range" name="backgroundOverlay" min="0" max="1" step="0.05"
              value={data.backgroundOverlay ?? 0.7}
              onChange={(e) => setData(prev => ({ ...prev, backgroundOverlay: parseFloat(e.target.value) }))}
              style={{ width: '100%', cursor: 'pointer' }} />
          </>
        ) : (
          <>
            <label>Background Color</label>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <input type="color" name="backgroundColor" value={data.backgroundColor || '#141414'}
                onChange={handleChange} style={{ width: '50px', height: '36px', border: 'none', cursor: 'pointer', backgroundColor: 'transparent' }} />
              <input type="text" name="backgroundColor" value={data.backgroundColor || '#141414'}
                onChange={handleChange} placeholder="#141414" style={{ flex: 1 }} />
            </div>
          </>
        )}

        <h4 style={{ marginTop: '24px', marginBottom: '8px', color: '#9a51e2', borderBottom: '1px solid #333', paddingBottom: '8px' }}>
          Button & Colors
        </h4>

        <label>Button Enabled</label>
        <div className="radio-group">
          <input type="radio" id={`${isEdit ? 'e' : 'n'}BtnY`} checked={data.buttonEnabled !== false}
            onChange={() => setData(prev => ({ ...prev, buttonEnabled: true }))} />
          <label htmlFor={`${isEdit ? 'e' : 'n'}BtnY`} className={`radio-label ${data.buttonEnabled !== false ? 'selected' : ''}`}>Enabled</label>
          <input type="radio" id={`${isEdit ? 'e' : 'n'}BtnN`} checked={data.buttonEnabled === false}
            onChange={() => setData(prev => ({ ...prev, buttonEnabled: false }))} />
          <label htmlFor={`${isEdit ? 'e' : 'n'}BtnN`} className={`radio-label ${data.buttonEnabled === false ? 'selected' : ''}`}>Disabled</label>
        </div>

        <label>Button Text</label>
        <input type="text" name="buttonText" value={data.buttonText} onChange={handleChange} placeholder="Play Now" />

        {data.buttonEnabled === false && (
          <>
            <label>Disabled Button Text</label>
            <input type="text" name="buttonDisabledText" value={data.buttonDisabledText || ''} onChange={handleChange} placeholder="Coming Soon" />
          </>
        )}

        <label>Button Color</label>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <input type="color" name="buttonColor" value={data.buttonColor || '#ffffff'}
            onChange={handleChange} style={{ width: '50px', height: '36px', border: 'none', cursor: 'pointer', backgroundColor: 'transparent' }} />
          <input type="text" name="buttonColor" value={data.buttonColor || '#ffffff'}
            onChange={handleChange} placeholder="#ffffff" style={{ flex: 1 }} />
        </div>

        <label>Button Text Color</label>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <input type="color" name="buttonTextColor" value={data.buttonTextColor || '#111111'}
            onChange={handleChange} style={{ width: '50px', height: '36px', border: 'none', cursor: 'pointer', backgroundColor: 'transparent' }} />
          <input type="text" name="buttonTextColor" value={data.buttonTextColor || '#111111'}
            onChange={handleChange} placeholder="#111111" style={{ flex: 1 }} />
        </div>

        <label>Title Color</label>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <input type="color" name="titleColor" value={data.titleColor || '#ffffff'}
            onChange={handleChange} style={{ width: '50px', height: '36px', border: 'none', cursor: 'pointer', backgroundColor: 'transparent' }} />
          <input type="text" name="titleColor" value={data.titleColor || '#ffffff'}
            onChange={handleChange} placeholder="#ffffff" style={{ flex: 1 }} />
        </div>

        <label>Description Color</label>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <input type="color" name="descriptionColor" value={data.descriptionColor || '#a6a6a6'}
            onChange={handleChange} style={{ width: '50px', height: '36px', border: 'none', cursor: 'pointer', backgroundColor: 'transparent' }} />
          <input type="text" name="descriptionColor" value={data.descriptionColor || 'rgba(255, 255, 255, 0.65)'}
            onChange={handleChange} placeholder="rgba(255, 255, 255, 0.65)" style={{ flex: 1 }} />
        </div>

        <h4 style={{ marginTop: '24px', marginBottom: '8px', color: '#9a51e2', borderBottom: '1px solid #333', paddingBottom: '8px' }}>
          SEO
        </h4>

        <label>SEO Title</label>
        <input type="text" name="metaTitle" value={data.metaTitle} onChange={handleChange} placeholder="Page title for search engines" />

        <label>SEO Description</label>
        <textarea name="metaDescription" value={data.metaDescription} onChange={handleChange} placeholder="Page description for search engines" />

        {/* URL preview */}
        {data.slug && (
          <div style={{
            marginTop: '20px', padding: '16px', backgroundColor: '#181828',
            borderRadius: '8px', border: '1px solid #555'
          }}>
            <label style={{
              display: 'block', marginBottom: '8px', marginTop: '0', fontWeight: '500',
              color: '#9a51e2', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.5px'
            }}>
              {isEdit ? 'Public Landing Page URL' : 'Landing Page URL Preview'}
            </label>
            <p style={{
              wordBreak: 'break-all', margin: '8px 0', padding: '14px 16px',
              backgroundColor: '#23243a', borderRadius: '8px', fontFamily: 'monospace',
              fontSize: '14px', color: '#e2e8f0', border: '1px solid #555'
            }}>
              {`${window.location.origin}/${data.slug}`}
            </p>
            {isEdit && (
              <button type="button" onClick={() => {
                navigator.clipboard.writeText(`${window.location.origin}/${data.slug}`);
                alert('URL copied to clipboard!');
              }} style={{
                padding: '14px 28px', backgroundColor: '#9333ea', color: 'white',
                border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '15px',
                fontWeight: '600', marginTop: '12px'
              }}>
                Copy URL
              </button>
            )}
          </div>
        )}
      </>
    );
  };

  return (
    <div className="form-group">
      <h2>Landing Pages</h2>

      {selectedLP ? (
        <div className="form-group">
          <h3>Update Landing Page: /{selectedLP.slug}</h3>
          {renderForm(selectedLP, setSelectedLP, true)}
          <button onClick={updateLandingPage}>Update Landing Page</button>
          <button style={{ backgroundColor: 'red' }} onClick={() => deleteLandingPage(selectedLP._id)}>Delete Landing Page</button>
          <button onClick={() => setSelectedLP(null)}>Back to List</button>
        </div>
      ) : (
        <>
          <div className="filters">
            <label>
              Filter by Slug:
              <input type="text" value={slugFilter} onChange={(e) => setSlugFilter(e.target.value)} placeholder="Search by slug..." />
            </label>
            <label>
              Filter by Status:
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">All</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </label>
          </div>

          <table>
            <thead>
              <tr>
                <th onClick={() => sortLandingPages('slug')}>Slug</th>
                <th onClick={() => sortLandingPages('productTitle')}>Product</th>
                <th onClick={() => sortLandingPages('isActive')}>Status</th>
                <th onClick={() => sortLandingPages('createdAt')}>Created</th>
              </tr>
            </thead>
            <tbody>
              {sortedLandingPages.map((lp) => (
                <tr key={lp._id} onClick={() => setSelectedLP({ ...lp, isActive: typeof lp.isActive === 'boolean' ? lp.isActive : true })}>
                  <td style={{ fontFamily: 'monospace' }}>/{lp.slug}</td>
                  <td>{lp.product?.title || 'N/A'}</td>
                  <td>
                    <span style={{
                      padding: '6px 16px', borderRadius: '20px', fontSize: '12px', fontWeight: '600',
                      background: lp.isActive ? '#48bb78' : '#f56565', color: 'white',
                      display: 'inline-block', minWidth: '70px', textAlign: 'center'
                    }}>
                      {lp.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{lp.createdAt ? new Date(lp.createdAt).toLocaleDateString() : 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h3>Create New Landing Page</h3>
          <div className="form-group">
            {renderForm(newLP, setNewLP, false)}
            <button onClick={createLandingPage}>Create Landing Page</button>
          </div>
        </>
      )}
    </div>
  );
};

export default ManageLandingPages;
